import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAxiosPrivate } from "@hooks/useAxiosPrivate";
import { socket } from "@api/socket";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "./notificationsApi";

const DEFAULT_LIMIT = 30;

export const useNotifications = ({ typeFilter = "alerts" } = {}) => {
  const axios = useAxiosPrivate();
  const company_id = useSelector((state) => state.user.companyId);
  const userId = useSelector((state) => state.user.user);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const lastFetchAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const doFetch = useCallback(
    async ({ silent = false } = {}) => {
      if (!company_id) return;
      if (inFlightRef.current) return;

      const now = Date.now();
      // avoid bursts (socket can emit fast)
      if (now - lastFetchAtRef.current < 800) return;
      lastFetchAtRef.current = now;

      inFlightRef.current = true;
      if (!silent) setIsLoading(true);
      setError(null);

      try {
        const data = await fetchNotifications(axios, {
          company_id,
          limit: DEFAULT_LIMIT,
          skip: 0,
          unreadOnly: false,
          typeFilter,
        });
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      } catch (e) {
        setError(e);
      } finally {
        inFlightRef.current = false;
        if (!silent) setIsLoading(false);
      }
    },
    [axios, company_id, typeFilter]
  );

  const POLL_WHEN_DISCONNECTED_MS = 60000;
  const POLL_WHEN_CONNECTED_MS = 300000; // 5 min when socket healthy
  const pollIntervalRef = useRef(null);

  const schedulePoll = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    const ms = socket.connected ? POLL_WHEN_CONNECTED_MS : POLL_WHEN_DISCONNECTED_MS;
    pollIntervalRef.current = setInterval(() => doFetch({ silent: true }), ms);
  }, [doFetch]);

  // Initial fetch + polling; when socket connect/disconnect switch interval
  useEffect(() => {
    doFetch();
    schedulePoll();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [doFetch, schedulePoll]);

  // On connect: resync once and use longer poll interval
  useEffect(() => {
    const onConnect = () => {
      doFetch({ silent: true });
      schedulePoll();
    };
    const onDisconnect = () => schedulePoll();
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [doFetch, schedulePoll]);

  // Join user-specific room and listen for real-time notification push
  useEffect(() => {
    if (!userId) return;

    const emitJoin = () => {
      socket.emit("joinUserRoom", userId);
    };

    if (socket.connected) {
      emitJoin();
    } else {
      socket.once("connect", emitJoin);
    }

    return () => {
      socket.off("connect", emitJoin);
    };
  }, [userId]);

  // Direct notification socket event: merge by _id, use data.messageCount for unread delta
  useEffect(() => {
    const handleNotification = (notification) => {
      if (!notification || !notification._id) return;

      const isMessage = notification.type === "new_message";
      if (typeFilter === "messages" && !isMessage) return;
      if (typeFilter === "alerts" && isMessage) return;

      const normalized = {
        ...notification,
        _id: notification._id,
        read: notification.read ?? false,
        createdAt: notification.createdAt || new Date().toISOString(),
        last_activity_at: notification.last_activity_at,
        data: notification.data || {},
      };
      const messageCount = Number(normalized.data?.messageCount) || 1;
      let unreadDelta = 0;
      setNotifications((prev) => {
        const idx = prev.findIndex((n) => String(n._id) === String(normalized._id));
        if (idx >= 0) {
          const old = prev[idx];
          const oldCount = Number(old?.data?.messageCount) || 1;
          unreadDelta = normalized.read ? 0 : Math.max(0, messageCount - oldCount);
          const next = [...prev];
          next[idx] = normalized;
          return next;
        }
        unreadDelta = normalized.read ? 0 : messageCount;
        return [normalized, ...prev];
      });
      setUnreadCount((c) => c + unreadDelta);
    };
    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, [typeFilter]);

  // When backend auto-marks notifications read (e.g. user opened conversation)
  useEffect(() => {
    const handleNotificationRead = (payload) => {
      if (payload?.typeFilter) {
        if (payload.typeFilter !== typeFilter) return;
      } else if (payload?.type) {
        const isMessage = payload.type === "new_message";
        if (typeFilter === "messages" && !isMessage) return;
        if (typeFilter === "alerts" && isMessage) return;
      }

      const ids = Array.isArray(payload?.notification_ids)
        ? payload.notification_ids
        : payload?.notification_id
          ? [payload.notification_id]
          : [];
      if (ids.length === 0) return;
      setNotifications((prev) =>
        prev.map((n) =>
          ids.includes(String(n._id)) ? { ...n, read: true } : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - ids.length));
    };
    socket.on("notificationRead", handleNotificationRead);
    return () => socket.off("notificationRead", handleNotificationRead);
  }, [typeFilter]);

  const markRead = useCallback(
    async (notification_id) => {
      if (!notification_id) return;

      // optimistic update
      setNotifications((prev) =>
        prev.map((n) =>
          String(n._id) === String(notification_id) ? { ...n, read: true } : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));

      try {
        await markNotificationRead(axios, { notification_id });
      } catch (e) {
        // revert by refetch (keeps logic simple and consistent)
        doFetch({ silent: true });
      }
    },
    [axios, doFetch]
  );

  const markAllRead = useCallback(async () => {
    if (!company_id) return;

    // optimistic
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await markAllNotificationsRead(axios, { company_id, typeFilter });
    } catch (e) {
      doFetch({ silent: true });
    }
  }, [axios, company_id, doFetch, typeFilter]);

  const hasNotifications = useMemo(() => notifications.length > 0, [notifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasNotifications,
    refetch: doFetch,
    markRead,
    markAllRead,
  };
};

